import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, X, ChevronRight, ChevronDown, MoreVertical,
  Copy, Building2, Pencil, Trash2, QrCode, Info,
  FolderOpen, FolderPlus, Search, Upload, Download, Share2, Home,
  Eye, Layers, Image, GraduationCap, Mail, Check, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESPACE_TABS = [
  { id: 'acces', label: 'Accès' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'documents', label: 'Documents' },
  { id: 'elearning', label: 'E-learning' },
];

export const SessionEspaceApprenantTab = ({
  form, update, sessionId,
  docFolders, docFiles, selectedFolder, setSelectedFolder,
  showShareModal, setShowShareModal, shareDocId, setShareDocId,
  shareSearch, setShareSearch, shareWith, setShareWith,
  shareUsers, setShareUsers, uploadingDoc,
  handleUploadDoc, handleDeleteDoc, handleShareDoc,
  handleCreateFolder, handleDeleteFolder,
  elSequences, elParcours, elSelectedParcours, setElSelectedParcours,
  showSequenceEditor, setShowSequenceEditor,
  showAddSequence, setShowAddSequence,
  handleCreateSequence, handleUpdateSequence, handleDeleteSequence,
  handleAddActivity, handleCreateParcours, handleDeleteParcours,
  fetchElSequences,
}) => {
  const [espaceTab, setEspaceTab] = useState('acces');

  return (
    <div className="space-y-6" data-testid="tab-espace-apprenant-content">
      {/* Sub-tabs */}
      <div className="flex rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
        {ESPACE_TABS.map(tab => (
          <button key={tab.id}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${espaceTab === tab.id ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border-b-2 border-teal-500' : 'bg-white dark:bg-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
            onClick={() => setEspaceTab(tab.id)} data-testid={`espace-tab-${tab.id}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ ACCÈS TAB ═══ */}
      {espaceTab === 'acces' && (
        <div className="space-y-10" data-testid="espace-acces-content">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Accès sécurisé</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">L'Espace Apprenant de la session est accessible via le lien de connexion générique ci-dessous :</p>
            <div className="flex items-center gap-3 flex-wrap mb-6">
              <code className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 font-mono" data-testid="access-link">
                {`${process.env.REACT_APP_BACKEND_URL}/apprenant/session/${sessionId || 'nouveau'}`}
              </code>
              <Button variant="outline" size="sm" className="text-xs h-9 border-slate-300 gap-1.5" data-testid="copy-access-link"
                onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/apprenant/session/${sessionId || 'nouveau'}`); toast.success('Lien copié !'); }}>
                <Copy className="h-3.5 w-3.5" />Copier
              </Button>
              <Button variant="outline" size="sm" className="text-xs h-9 border-slate-300 gap-1.5" data-testid="show-qr-code">
                <QrCode className="h-3.5 w-3.5" />Afficher le QR code
              </Button>
            </div>
            <div className="bg-violet-50/60 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4 flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center shrink-0 mt-0.5"><Info className="h-3.5 w-3.5 text-white" /></div>
              <div className="text-sm text-slate-700 dark:text-slate-300">
                <p className="font-medium">Cette méthode de connexion est la plus sécurisée.</p>
                <p className="text-slate-500 dark:text-slate-400 mt-1">L'apprenant doit avoir un email renseigné pour que cette méthode fonctionne.</p>
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Accès par lien personnel</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">Les extranets personnels sont accessibles via les liens ci-dessous</p>
            <div className="bg-violet-50/60 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4 flex items-start gap-3 mb-6">
              <div className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center shrink-0 mt-0.5"><Info className="h-3.5 w-3.5 text-white" /></div>
              <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">Les liens personnels sont moins sécurisés que l'accès par email.</p>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs text-slate-400 italic">Jamais généré</span>
              <Button variant="outline" size="sm" className="text-xs h-8 border-slate-300 gap-1.5" data-testid="qr-codes-dropdown">
                <QrCode className="h-3 w-3" />Liste des QR codes<ChevronDown className="h-3 w-3" />
              </Button>
            </div>
            {form.apprenants.length > 0 ? (
              <div className="space-y-2">
                {form.apprenants.map((a, i) => (
                  <div key={a.id || i} className="flex items-center gap-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3" data-testid={`access-apprenant-${i}`}>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-200 to-cyan-200 dark:from-teal-800 dark:to-cyan-800 flex items-center justify-center"><GraduationCap className="h-4 w-4 text-teal-600 dark:text-teal-300" /></div>
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 dark:text-white">{a.name}</p><p className="text-xs text-slate-400 truncate">{a.email || 'Email non renseigné'}</p></div>
                    <Badge variant="outline" className="text-[10px] text-slate-400">Non activé</Badge>
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/apprenant/session/${sessionId}?user=${a.id}`); toast.success('Lien copié'); }}><Copy className="h-3 w-3 mr-1" />Copier le lien</Button>
                    <Button variant="outline" size="sm" className="text-xs h-7"><Mail className="h-3 w-3 mr-1" />Envoyer</Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 py-6 text-center">Aucun apprenant inscrit pour cette session.</p>
            )}
          </div>
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700" data-testid="admin-access-section">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Accès administrateur</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Connectez-vous en tant qu'administrateur pour prévisualiser l'Espace Apprenant.</p>
            <Button variant="outline" className="text-sm h-10 border-slate-300" data-testid="admin-preview-btn"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/apprenant/session/${sessionId || 'nouveau'}?admin_preview=1`, '_blank')}>
              Me connecter à l'extranet en tant qu'administrateur
            </Button>
          </div>
        </div>
      )}

      {/* ═══ CONFIGURATION TAB ═══ */}
      {espaceTab === 'configuration' && (
        <div className="space-y-0 divide-y divide-slate-200 dark:divide-slate-700" data-testid="espace-configuration-content">
          <div className="pb-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Affichage</h2>
            <div className="space-y-5">
              <div>
                <label className="text-sm text-slate-500 dark:text-slate-400 mb-1.5 block">Nom de la session pour les apprenants</label>
                <Input value={form.learner_session_display_name} onChange={(e) => update('learner_session_display_name', e.target.value)} placeholder={form.title || 'Intitulé de la session'} className="h-11" data-testid="learner-session-display-name" />
              </div>
              <div className="flex items-start gap-4 bg-violet-50/40 dark:bg-violet-900/10 rounded-lg p-4">
                <div className="w-16 h-16 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center shrink-0"><Image className="h-7 w-7 text-violet-400" /></div>
                <div><p className="text-sm font-semibold text-slate-900 dark:text-white">Image de couverture</p><p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Ratio 4/1, format JPG, PNG ou WebP</p></div>
              </div>
            </div>
          </div>
          <div className="py-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Pages</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Activez les pages que vous souhaitez afficher dans l'Espace Apprenant.</p>
            <div className="space-y-3">
              {[
                { key: 'page_accueil', label: 'Page d\'accueil', desc: 'Accueil de la session', icon: Home },
                { key: 'page_programme', label: 'Programme', desc: 'Descriptif et programme de la session', icon: BookOpen },
                { key: 'page_emargement', label: 'Émargement', desc: 'Émargement en ligne des apprenants', icon: Check },
                { key: 'page_evaluations', label: 'Évaluations', desc: 'Évaluations et questionnaires', icon: GraduationCap },
                { key: 'page_documents', label: 'Documents', desc: 'Documents partagés de la session', icon: FolderOpen },
                { key: 'page_elearning', label: 'E-learning', desc: 'Séquences et parcours e-learning', icon: Layers },
              ].map(({ key, label, desc, icon: Icon }) => (
                <label key={key} className="flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 cursor-pointer hover:border-teal-200 dark:hover:border-teal-700 transition-colors" data-testid={`toggle-${key}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center"><Icon className="h-5 w-5 text-teal-500" /></div>
                    <div><p className="text-sm font-semibold text-slate-900 dark:text-white">{label}</p><p className="text-xs text-slate-400">{desc}</p></div>
                  </div>
                  <input type="checkbox" checked={form[key] !== false} onChange={(e) => update(key, e.target.checked)} className="h-5 w-5 rounded border-slate-300 text-teal-500 focus:ring-teal-500" />
                </label>
              ))}
            </div>
          </div>
          <div className="py-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Fonctionnalités</h2>
            <div className="space-y-3">
              {[
                { key: 'feature_forum', label: 'Forum de discussion', desc: 'Espace d\'échange entre les apprenants' },
                { key: 'feature_chat', label: 'Chat en direct', desc: 'Messagerie instantanée avec le formateur' },
                { key: 'feature_gamification', label: 'Gamification', desc: 'Système de points, badges et classement' },
                { key: 'feature_certificat', label: 'Certificat automatique', desc: 'Génération automatique du certificat à la fin' },
              ].map(({ key, label, desc }) => (
                <label key={key} className="flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 cursor-pointer" data-testid={`toggle-${key}`}>
                  <div><p className="text-sm font-semibold text-slate-900 dark:text-white">{label}</p><p className="text-xs text-slate-400">{desc}</p></div>
                  <input type="checkbox" checked={form[key] !== false} onChange={(e) => update(key, e.target.checked)} className="h-5 w-5 rounded border-slate-300 text-teal-500 focus:ring-teal-500" />
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ DOCUMENTS TAB ═══ */}
      {espaceTab === 'documents' && (
        <div className="space-y-4" data-testid="espace-documents-content">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Documents de la session</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleCreateFolder} data-testid="create-folder-btn"><FolderPlus className="h-3.5 w-3.5" />Créer un dossier</Button>
              <label>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white text-xs gap-1.5" size="sm" data-testid="upload-doc-btn" disabled={uploadingDoc}><Upload className="h-3.5 w-3.5" />{uploadingDoc ? 'Envoi...' : 'Ajouter un document'}</Button>
                <input type="file" className="hidden" onChange={handleUploadDoc} />
              </label>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setSelectedFolder('default')} className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${selectedFolder === 'default' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid="folder-default"><Home className="h-3 w-3 inline mr-1" />Racine</button>
            {docFolders.map(folder => (
              <div key={folder.folder_id} className="flex items-center gap-1">
                <button onClick={() => setSelectedFolder(folder.folder_id)} className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${selectedFolder === folder.folder_id ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid={`folder-${folder.folder_id}`}><FolderOpen className="h-3 w-3 inline mr-1" />{folder.name}</button>
                <button onClick={() => handleDeleteFolder(folder.folder_id)} className="text-slate-400 hover:text-red-500 p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
          {docFiles.filter(f => (f.folder_id || 'default') === selectedFolder).length === 0 ? (
            <div className="text-center py-10 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-600"><FolderOpen className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-400">Aucun document dans ce dossier.</p></div>
          ) : (
            <div className="space-y-2">
              {docFiles.filter(f => (f.folder_id || 'default') === selectedFolder).map((doc) => (
                <div key={doc.doc_id} className="flex items-center gap-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3" data-testid={`doc-row-${doc.doc_id}`}>
                  <div className="w-9 h-9 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center shrink-0"><Eye className="h-4 w-4 text-teal-500" /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-800 dark:text-white truncate">{doc.name}</p><p className="text-xs text-slate-400">{doc.type || 'Document'} • {doc.size || '—'}</p></div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setShareDocId(doc.doc_id); setShowShareModal(true); }}><Share2 className="h-3 w-3 mr-1" />Partager</Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleDeleteDoc(doc.doc_id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {showShareModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
                <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-4">Partager le document</h3>
                <div className="space-y-3">
                  <Select value={shareWith} onValueChange={setShareWith}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tous les apprenants</SelectItem><SelectItem value="selected">Apprenants sélectionnés</SelectItem></SelectContent></Select>
                  {shareWith === 'selected' && (
                    <div>
                      <Input value={shareSearch} onChange={(e) => setShareSearch(e.target.value)} placeholder="Rechercher un apprenant..." className="mb-2" />
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {(form.apprenants || []).filter(a => !shareSearch || a.name?.toLowerCase().includes(shareSearch.toLowerCase())).map((a, i) => (
                          <label key={i} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
                            <input type="checkbox" checked={shareUsers.includes(a.id)} onChange={(e) => setShareUsers(prev => e.target.checked ? [...prev, a.id] : prev.filter(id => id !== a.id))} className="rounded" />
                            <span className="text-sm">{a.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={() => setShowShareModal(false)}>Annuler</Button>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleShareDoc}>Partager</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ E-LEARNING TAB ═══ */}
      {espaceTab === 'elearning' && (
        <div className="space-y-6" data-testid="espace-elearning-content">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Séquences e-learning</h2>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white text-xs gap-1.5" onClick={() => setShowAddSequence(true)} data-testid="btn-add-sequence"><Plus className="h-4 w-4" />Ajouter une séquence</Button>
          </div>
          {elSequences.length === 0 ? (
            <div className="text-center py-10 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-600"><Layers className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-400">Aucune séquence e-learning.</p></div>
          ) : (
            <div className="space-y-3">
              {elSequences.map((seq) => (
                <div key={seq.seq_id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4" data-testid={`el-sequence-${seq.seq_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center"><GraduationCap className="h-5 w-5 text-teal-500" /></div>
                      <div><h4 className="font-bold text-sm text-slate-900 dark:text-white">{seq.title}</h4><p className="text-xs text-slate-400">{(seq.activities || []).length} activité(s)</p></div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowSequenceEditor(seq)}><Pencil className="h-3 w-3 mr-1" />Modifier</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:text-red-600" onClick={() => handleDeleteSequence(seq.seq_id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Parcours e-learning</h2>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white text-xs gap-1.5" onClick={handleCreateParcours} data-testid="btn-create-parcours"><Plus className="h-4 w-4" />Créer un parcours</Button>
            </div>
            {elParcours.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-300 dark:border-slate-600"><BookOpen className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p className="text-sm text-slate-400">Aucun parcours e-learning.</p></div>
            ) : (
              <div className="space-y-3">
                {elParcours.map((parc) => (
                  <div key={parc.parcours_id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4" data-testid={`el-parcours-${parc.parcours_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center"><BookOpen className="h-5 w-5 text-violet-500" /></div><div><h4 className="font-bold text-sm text-slate-900 dark:text-white">{parc.name}</h4><p className="text-xs text-slate-400">{(parc.sequence_ids || []).length} séquence(s)</p></div></div>
                      <Button variant="outline" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleDeleteParcours(parc.parcours_id)}><Trash2 className="h-3 w-3 mr-1" />Supprimer</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white mb-3">Période d'accès e-learning</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Date d'ouverture</label><Input type="date" value={form.elearning_open_date} onChange={(e) => update('elearning_open_date', e.target.value)} className="h-10" data-testid="elearning-open-date" /></div>
              <div><label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Date de fermeture</label><Input type="date" value={form.elearning_close_date} onChange={(e) => update('elearning_close_date', e.target.value)} className="h-10" data-testid="elearning-close-date" /></div>
            </div>
          </div>
        </div>
      )}

      {/* Add Sequence Modal */}
      {showAddSequence && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-4">Nouvelle séquence</h3>
            <div className="space-y-3">
              <Input id="new-seq-title" placeholder="Titre de la séquence" data-testid="input-new-seq-title" />
              <Input id="new-seq-desc" placeholder="Description (optionnel)" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowAddSequence(false)}>Annuler</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => {
                const title = document.getElementById('new-seq-title')?.value;
                const desc = document.getElementById('new-seq-desc')?.value;
                if (title) { handleCreateSequence({ title, description: desc || '' }); setShowAddSequence(false); }
                else toast.error('Titre requis');
              }} data-testid="confirm-create-sequence">Créer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Sequence Modal */}
      {showSequenceEditor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-lg shadow-xl">
            <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-4">Modifier la séquence</h3>
            <div className="space-y-3">
              <Input defaultValue={showSequenceEditor.title} id="edit-seq-title" placeholder="Titre" />
              <Input defaultValue={showSequenceEditor.description} id="edit-seq-desc" placeholder="Description" />
              <div>
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Activités ({(showSequenceEditor.activities || []).length})</h4>
                {(showSequenceEditor.activities || []).map((act, ai) => (
                  <div key={ai} className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700 rounded-lg p-2 mb-1"><span className="text-xs text-slate-600 dark:text-slate-300">{act.title || act.type}</span></div>
                ))}
                <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => handleAddActivity(showSequenceEditor.seq_id, { type: 'contenu', title: `Activité ${(showSequenceEditor.activities || []).length + 1}` })}><Plus className="h-3 w-3 mr-1" />Ajouter une activité</Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowSequenceEditor(null)}>Annuler</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => {
                const title = document.getElementById('edit-seq-title')?.value;
                const desc = document.getElementById('edit-seq-desc')?.value;
                handleUpdateSequence(showSequenceEditor.seq_id, { title, description: desc });
                setShowSequenceEditor(null);
              }}>Sauvegarder</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
